import { useUserPermissions } from '@folio/hooks/useUserPermissions';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type BranchDto, branchControllerEditBranch } from 'korint-sdk';
import { invalidatePolicyDashboardCache } from 'korint-sdk/react';
import { TextCursorInputIcon } from 'lucide-react';
import { type ReactNode, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../ui/button';
import { Input } from '../../forms/input/input';
import { Group, Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

export function BranchNameEditor({
  branch,
  trigger = 'icon',
  children,
}: {
  branch: BranchDto;
  trigger?: 'icon' | 'text';
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  const { policy } = usePolicyOnDashboard();
  const queryClient = useQueryClient();
  const { can } = useUserPermissions({ productId: policy.productId, actions: ['EDIT_BRANCH'] });
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(branch.name ?? '');
  const inputId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const closeEditor = () => {
    setEditing(false);
    requestAnimationFrame(() => triggerRef.current?.focus());
  };
  const rename = useMutation({
    mutationFn: (nextName: string) => branchControllerEditBranch(branch.id, { name: nextName }),
    onSuccess: async () => {
      await invalidatePolicyDashboardCache(queryClient, policy.id);
      closeEditor();
    },
  });

  useEffect(() => {
    setEditing(false);
    setName(branch.name ?? '');
  }, [branch.id]);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  if (!can.EDIT_BRANCH || branch.branchType !== 'MTA' || branch.branchStatus !== 'BRANCH_OPEN') {
    return children ?? null;
  }

  if (!editing) {
    return (
      <>
        {children}
        <Button
          ref={triggerRef}
          type="button"
          variant="ghost"
          size={trigger === 'icon' ? 'icon' : 'sm'}
          className={trigger === 'icon' ? 'h-8 w-8 shrink-0' : undefined}
          aria-label={t('branchContext.rename')}
          onClick={event => {
            event.stopPropagation();
            rename.reset();
            setName(branch.name ?? '');
            setEditing(true);
          }}
        >
          <TextCursorInputIcon className="h-4 w-4" aria-hidden="true" />
          {trigger === 'text' && <span className="ml-2">{t('branchContext.rename')}</span>}
        </Button>
      </>
    );
  }

  return (
    <Stack
      role="group"
      aria-label={t('branchContext.nameLabel')}
      className={children ? 'min-w-0 max-w-full gap-2 sm:w-[34rem]' : 'min-w-0 gap-2'}
      onClick={event => event.stopPropagation()}
      onKeyDown={event => {
        if (event.key === 'Enter' && event.target === inputRef.current) {
          event.preventDefault();
          event.stopPropagation();
          if (name.trim() && !rename.isPending) rename.mutate(name.trim());
        }
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          if (!rename.isPending) closeEditor();
        }
      }}
    >
      <label className="sr-only" htmlFor={inputId}>
        {t('branchContext.nameLabel')}
      </label>
      <Group wrap gap={2}>
        <Input
          ref={inputRef}
          id={inputId}
          className="h-9 min-w-40 flex-1"
          value={name}
          onChange={event => setName(event.target.value)}
          disabled={rename.isPending}
          aria-invalid={rename.isError}
          aria-describedby={rename.isError ? `${inputId}-error` : undefined}
          required
        />
        <Button
          type="button"
          size="sm"
          disabled={!name.trim()}
          loading={rename.isPending}
          onClick={() => rename.mutate(name.trim())}
        >
          {t('branchContext.save')}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={rename.isPending}
          onClick={closeEditor}
        >
          {t('branchContext.cancel')}
        </Button>
      </Group>
      {rename.isError && (
        <Text size="sm" tone="destructive" id={`${inputId}-error`} role="alert">
          {t('branchContext.renameError')}
        </Text>
      )}
    </Stack>
  );
}
